import React, { useEffect, useState } from "react";
import { ReactComponent as PaginationPrevIcon } from "../../../assets/img/icons/arrow-prev-pagination.svg";
import { ReactComponent as PaginationNextIcon } from "../../../assets/img/img-09-10-23/arrow-next-pagination-2.svg";
import { Course } from "./Course";
import { useSelector, useDispatch } from "react-redux";
import { Loader } from "../../../components/ui/Loader";
import { useSearchParams } from "react-router-dom";
import { formatDate } from "../../../helpers";
import { BookCourseModal } from "../../../components/modals/BookCourseModal";
import { trackFunnel } from "../../../helpers/analytics";

export const Courses = () => {
  const grouped = useSelector((state) => state.courses.grouped);

  const coursesLoading = useSelector((state) => state.courses.coursesLoading);
  const courses = useSelector((state) => state.courses.courses);
  // every date at the "Virtual" centre = a live online course: no venues, so no "closest to" wording
  const onlineOnly =
    grouped?.length > 0 && grouped.every((g) => g?.center?.name === "Virtual");

  const [bookCourseModalOpen, setBookCourseModalOpen] = useState(false);

  const [params] = useSearchParams();
  const [filteredCourses, setFilteredCourses] = useState([...grouped]);
  const [itemsOnPage, setItemsOnPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    filterCourses();
  }, [grouped, coursesLoading]);

  const filterCourses = () => {
    let copy = [...grouped];
    const filteredGroupedCopy = copy?.map((item) => {
      const schedules = item?.schedules
        ?.filter((item) => {
          let aDate = +params?.get("start_date") || Date.now();
          let bDate = new Date(item.start_date * 1000);
          return new Date(aDate) <= new Date(bDate);
        })
        .sort((a, b) => a.start_date - b.start_date);
      return {
        center: item?.center,
        schedules,
      };
    });


    setFilteredCourses(filteredGroupedCopy);
  };

  const getPaginatedGroup = () => {
    let paginatedGroup = [...filteredCourses].slice(
      (currentPage - 1) * itemsOnPage,
      currentPage * itemsOnPage,
    );
    return paginatedGroup;
  };

  const closestToCourses = getPaginatedGroup().filter(
    (item) =>
      item?.center?.name?.split(" ")[0] ===
      getPaginatedGroup()[0]?.center?.name?.split(" ")[0],
  );
  const closestToCoursesCities = closestToCourses?.map(
    (item) => item?.center?.name?.split(" ")[0],
  );

  // Onsite recovery prompt: only when this product is actually delivered onsite
  // AND the nearest in-person venue is far enough away to be inconvenient.
  const ONSITE_PROMPT_MILES = 40;
  const nearestVenueMiles = (() => {
    const ds = grouped
      .map((g) => Number(g?.center?.distance))
      .filter((d) => Number.isFinite(d) && d > 0);
    return ds.length ? Math.min(...ds) * 0.62137119 : null;
  })();
  const onsiteAvailable = courses?.[0]?.onSiteCourse === true;
  const showOnsitePrompt =
    onsiteAvailable &&
    nearestVenueMiles != null &&
    nearestVenueMiles > ONSITE_PROMPT_MILES;

  useEffect(() => {
    if (showOnsitePrompt)
      trackFunnel("onsite_prompt_shown", {
        nearest_miles: Math.round(nearestVenueMiles),
      });
  }, [showOnsitePrompt, params.get("location")]);

  useEffect(() => {
    if (document.querySelector("#courses")) {
      document.querySelector("#courses").scrollIntoView({
        block: "center",
        inline: "center",
        behavior: "smooth",
      });
    }
  }, []);
  if (coursesLoading) {
    return <Loader />;
  }
  if (!getPaginatedGroup()?.length) {
    return null;
  }

  return (
    <>
      <div id="courses"></div>
      {showOnsitePrompt && (
        <div className="onsite-recovery mb-24">
          <div className="onsite-recovery__text">
            <span className="onsite-recovery__eyebrow">
              Nearest venue {Math.round(nearestVenueMiles)} miles away
            </span>
            <h4 className="heading-5 onsite-recovery__title">
              No venue near you?
            </h4>
            <p className="onsite-recovery__desc">
              We can deliver {courses?.[0]?.name || "this course"} onsite at your
              workplace — anywhere in the UK, on a date that suits you.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-success btn--md radius-5 ttu onsite-recovery__cta"
            onClick={() => {
              trackFunnel("onsite_prompt_clicked", {
                nearest_miles: Math.round(nearestVenueMiles),
              });
              setBookCourseModalOpen(true);
            }}
          >
            Enquire about onsite training
          </button>
        </div>
      )}
      {filteredCourses.length > 0 && !onlineOnly && (
        <div className="pill-message text-center mb-15">
          <p>
            We have shown you the {filteredCourses?.length} closest venues based
            on your estimated location.
          </p>
        </div>
      )}
      <div className="items items-24">
        <div className="result-search">
          {getPaginatedGroup()[0] && (
            <div className="result-search-title">
              <h4 className="heading-4">
                {onlineOnly ? "Live online dates" : `Closest to ${params.get("location")}`}
              </h4>
            </div>
          )}
          <div className="result-search-body">
            <ul className="result-search-list">
              {closestToCourses?.map((item, index) => {
                return (
                  <li key={index}>
                    <Course group={item} closest={params.get("location")} />
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>

      <div className="pill-message-2 mb-24">
        <p>
          If suitable dates are not available please contact our customer
          service team on{" "}
          <a href="tel:08081642780" className="btn tdu color-text--700">
            0808 164 2780
          </a>
          <br />
          For courses that are sold out, we can add you to our waiting list for
          any cancellations.
        </p>
      </div>

      <div className="items items-24">
        <div className="result-search">
          {getPaginatedGroup().length > 0 && !onlineOnly && (
            <div className="result-search-title">
              <h4 className="heading-4">Check other locations</h4>
            </div>
          )}
          <div className="result-search-body">
            <ul className="result-search-list">
              {getPaginatedGroup().map((group, index) => {
                // closestToCoursesCities
                // if (index !== 0)
                if (
                  !closestToCoursesCities.includes(
                    group?.center?.name?.split(" ")[0],
                  )
                )
                  return (
                    <li key={index + group.center.name}>
                      <Course
                        group={group}
                        bookOnsite={() => {
                          setBookCourseModalOpen(true);
                        }}
                      />
                    </li>
                  );
              })}
            </ul>
          </div>
          {Math.ceil(grouped?.length / itemsOnPage) > 1 && (
            <div className="result-search-footer">
              <div className="pagination-no-responsive justify-content-center">
                <button
                  className={`btn ${currentPage === 1 ? "btn--didabled" : ""}`}
                  onClick={() => setCurrentPage((prev) => (prev = prev - 1))}
                >
                  <span className="btn-icon me-2 d-flex">
                    <PaginationPrevIcon />
                  </span>
                  Prev
                </button>
                <ul className="pagination-list">
                  {Array.from(
                    { length: Math.ceil(grouped?.length / itemsOnPage) },
                    (_, index) => (
                      <li key={index}>
                        <button
                          className={`pagination-link ${
                            index + 1 === currentPage ? "active" : ""
                          }`}
                          onClick={() => setCurrentPage(index + 1)}
                        >
                          {index + 1}
                        </button>
                      </li>
                    ),
                  )}
                </ul>
                <button
                  className={`btn ${
                    currentPage === Math.ceil(grouped?.length / itemsOnPage)
                      ? "btn--didabled"
                      : ""
                  }`}
                  onClick={() => setCurrentPage((prev) => (prev = prev + 1))}
                >
                  Next
                  <span className="btn-icon ms-2 d-flex">
                    <PaginationNextIcon />
                  </span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {bookCourseModalOpen ? (
        <BookCourseModal
          isOpen={bookCourseModalOpen}
          close={() => setBookCourseModalOpen(false)}
        />
      ) : null}
    </>
  );
};
